import {
  DateRangeSelector,
  ENUM_DATE_RANGE,
  getDateRangeSelector,
  SideModal
} from '@cokitchen/cokitchen-components';
import { ArrowUpCircleIcon, NairaIcon, SurgeFilledIcon } from 'cokitchen-icons';
import React, { useEffect, useState } from 'react';
import { ENUM_PERFORMANCE_FILTERS } from 'utils/enums';
import PerformanceOverviewTable from './performance-overview-table';
import useAxios from 'hooks/useAxios';
import { addDays, differenceInDays, formatDistanceStrict, subDays } from 'date-fns';
import { handleApiError } from 'utils/functions';
import { OverviewAnalyticsType } from 'models/deals.model';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const PerformanceOverviewModal = ({ isOpen, onClose }: Props) => {
  const [performaceFilter, setPerformanceFilter] = useState({
    filter: ENUM_PERFORMANCE_FILTERS.TOTAL,
    startDate: getDateRangeSelector(ENUM_DATE_RANGE.ALL).startDate.toISOString(),
    endDate: getDateRangeSelector(ENUM_DATE_RANGE.ALL).endDate.toISOString()
  });

  const getDatesValue = () => {
    const dayDifference =
      differenceInDays(new Date(performaceFilter?.endDate), new Date(performaceFilter?.startDate)) +
      1;
    const prevStartDate = subDays(new Date(performaceFilter?.startDate), dayDifference);
    const prevEndDate = addDays(new Date(prevStartDate), dayDifference);
    const today = new Date();

    return {
      endDate: today?.toISOString(),
      startDate: performaceFilter?.startDate,
      previousStartDate: prevStartDate?.toISOString(),
      previousEndDate: prevEndDate?.toISOString()
    };
  };

  const {
    fetchData: fetchOverviewAnalytics,
    loading: loadingOverviewAnalytics,
    response: overviewAnalyticsData
  } = useAxios<OverviewAnalyticsType>({
    url: `internal/deals/performance/metrics?start_date=${getDatesValue()?.startDate}&end_date=${
      getDatesValue()?.endDate
    }&previous_start_date=${getDatesValue()?.previousStartDate}&previous_end_date=${
      getDatesValue()?.previousEndDate
    }`,
    isInternal: true
  });

  const handleFetchAnalytic = async () => {
    try {
      await fetchOverviewAnalytics();
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    handleFetchAnalytic();
  }, [performaceFilter.startDate, performaceFilter.endDate]);

  const hasPositiveSalesPercent =
    Number(overviewAnalyticsData?.data?.percentage_change_total_usage) > 0;
  const hasPositiveUsagePercent =
    Number(overviewAnalyticsData?.data?.percentage_change_total_usage) > 0;

  return (
    <SideModal
      bodyClassName='sm:!w-[50vw] 2xl:!w-[40vw] !pt-5'
      title={
        <span className='p-1.5 bg-blue-1/70 rounded-full inline-flex '>
          <SurgeFilledIcon className='text-blue-5 bg-blue-2/60 rounded-full w-7 h-7 p-1' />
        </span>
      }
      isOpen={isOpen}
      onRequestClose={onClose}>
      <section>
        <h2 className='text-xl font-extrabold mt-2'>Performance overview</h2>
        <p className='text-sm text-gray-8/90 font-medium'>
          A report of the performance of all deal & discount
        </p>
        <div className='mt-10 flex items-center justify-between'>
          <div className='flex items-center px-2 py-1.5 rounded-full border border-gray-1 bg-gray-2/40 w-fit'>
            {Object.values(ENUM_PERFORMANCE_FILTERS).map((filter) => (
              <div
                onClick={() => setPerformanceFilter({ ...performaceFilter, filter })}
                key={filter}
                className={`transition-all cursor-pointer text-gray-4 text-xs py-1 px-3.5 rounded-2xl first-letter:capitalize font-medium ${
                  filter === performaceFilter?.filter ? 'bg-gray-7 text-white' : ''
                }`}>
                {filter === ENUM_PERFORMANCE_FILTERS.AVG ? 'Avg' : filter}
              </div>
            ))}
          </div>
          <DateRangeSelector
            defaultRange={ENUM_DATE_RANGE.ALL}
            onChange={(v) =>
              setPerformanceFilter({
                ...performaceFilter,
                startDate: v.date.startDate.toISOString(),
                endDate: v.date.endDate.toISOString()
              })
            }
            className='h-9 text-gray-6/70 border-gray-1 font-medium text-sm w-fit'
          />
        </div>
        <div className='flex gap-5 mt-8'>
          <div
            className={`bg-[url("/src/assets/icons/performance-sales.svg")] bg-no-repeat bg-right flex-1 border-gray-1/70 border rounded-xl overflow-hidden ${
              loadingOverviewAnalytics ? 'opacity-40' : ''
            }`}>
            <div className='bg-gray-1/10 p-5 py-8'>
              <h4 className='font-semibold text-gray-5 text-sm mb-2'>TOTAL SALES FROM D&D</h4>
              <p
                className='flex items-center text-gray-7 font-extrabold
             text-3xl'>
                <NairaIcon className='text-gray-5 w-6 h-6 mr-1 ' strokeWidth={20} fontSize={20} />
                {overviewAnalyticsData?.data?.total_revenue_across_discounts.toLocaleString() ?? 0}
                <span className='text-sm self-end font-semibold relative -top-1'>.00</span>
              </p>
              <p
                className={`w-fit rounded-2xl p-1  ${
                  hasPositiveSalesPercent ? 'bg-green-5/10 text-green-5' : 'bg-red-5/10 text-red-5'
                } mt-4 flex items-center  gap-2 px-3`}>
                <ArrowUpCircleIcon
                  className={`${
                    hasPositiveSalesPercent ? 'text-green-5' : 'text-red-5  rotate-180'
                  }`}
                />
                <span className='font-semibold text-sm flex gap-2'>
                  {Math.abs(Number(overviewAnalyticsData?.data?.percentage_change_total_revenue))}%
                  <span>
                    {formatDistanceStrict(
                      new Date(performaceFilter?.startDate),
                      new Date(performaceFilter?.endDate)
                    )}
                    {' ago'}
                  </span>
                </span>
              </p>
            </div>
          </div>
          <div
            className={`bg-[url("/src/assets/icons/performance-usage.svg")] bg-no-repeat bg-right flex-1  border-gray-1/70 border rounded-xl overflow-hidden ${
              loadingOverviewAnalytics ? 'opacity-40' : ''
            }`}>
            <div className='bg-gray-1/10 p-5 py-8'>
              <h4 className='font-semibold text-gray-5 text-sm mb-2'>TOTAL USAGE</h4>
              <p
                className='flex items-center text-gray-7 font-extrabold
             text-3xl'>
                {overviewAnalyticsData?.data?.total_usage_across_discounts.toLocaleString() ?? 0}
              </p>
              <p
                className={`w-fit rounded-2xl p-1 ${
                  hasPositiveUsagePercent ? 'bg-green-5/10' : 'bg-red-5/10'
                }  mt-4 flex items-center ${
                  hasPositiveUsagePercent ? 'text-green-5' : 'text-red-5'
                } gap-2 px-3`}>
                <ArrowUpCircleIcon
                  className={`${
                    hasPositiveUsagePercent ? 'text-green-5' : 'text-red-5  rotate-180'
                  } `}
                />
                <span className='font-semibold text-sm flex gap-2'>
                  {Math.abs(Number(overviewAnalyticsData?.data?.percentage_change_total_usage))}%
                  <span>
                    {formatDistanceStrict(
                      new Date(performaceFilter?.startDate),
                      new Date(performaceFilter?.endDate)
                    )}
                    {' ago'}
                  </span>
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className='mt-10'>
          <PerformanceOverviewTable
            loading={loadingOverviewAnalytics}
            analyticsData={overviewAnalyticsData?.data?.deal_breakdowns ?? []}
          />
        </div>
      </section>
    </SideModal>
  );
};
export default PerformanceOverviewModal;
